import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { modifier } from 'ember-modifier';
import { restartableTask, timeout } from 'ember-concurrency';
import { loadTinyMCE } from '@linc-technologies/linced-common/utils/tinymce-loader';

// DEFAULT_TINY_EVENTS are the editor events that trigger a debounced
// content-change check.
const DEFAULT_TINY_EVENTS = 'change keyup keydown keypress mousedown';

// DEFAULT_CHANGE_DEBOUNCE is the debounce window (ms) for content changes.
const DEFAULT_CHANGE_DEBOUNCE = 15;

// TinymceEditor provides a text editor that displays content and feeds content
// changes back via callbacks.
//
//   - init/teardown run from the `setupEditor` element modifier.
//   - `@value` and `@options` changes are handled by `updateValue`/`updateOptions`.
//   - `@alwaysForwardContent` forwards content on every change, bypassing the
//     isDirty()/value-equality guards.
//   - `@preserveCaret` saves and restores the selection bookmark around an
//     external `@value` update so an echoed autosave value doesn't move the
//     caret.
export default class TinymceEditor extends Component {
  // editor holds the component's TinyMCE instance once initialised.
  editor = null;

  // appliedOptions snapshots the options currently applied to the editor to
  // detect changes.
  appliedOptions = null;

  // initToken identifies the current initialisation. Every initEditor call
  // bumps it; a load step or init callback whose token is stale belongs to a
  // superseded init (e.g. @options changed mid-init) and must not adopt its
  // editor — TinyMCE calls init_instance_callback on a later tick, so a swapped
  // config can otherwise leave the old editor active or an editor undisposed.
  initToken = 0;

  // tinyEvents configures the browser events the editor will listen for.
  get tinyEvents() {
    return this.args.tinyEvents ?? DEFAULT_TINY_EVENTS;
  }

  // changeDebounce configures the amount of time to
  get changeDebounce() {
    return this.args.changeDebounce ?? DEFAULT_CHANGE_DEBOUNCE;
  }

  // setupEditor initialises TinyMCE against the rendered <textarea> and disposes
  // it on teardown. It reads args only outside a tracked frame (via the async
  // initEditor), so it runs exactly once per element lifecycle.
  setupEditor = modifier((element) => {
    this.appliedOptions = this.args.options;
    this.initEditor(element, this.args.options);

    return () => this.teardownEditor();
  });

  // updateValue reacts to external @value changes, pushing them into the editor.
  // An update the editor already reflects is skipped so we don't reset the
  // caret needlessly.
  updateValue = modifier((element, [value]) => {
    let editor = this.editor;
    if (!editor || editor.getContent() === value) {
      return;
    }

    if (!this.args.preserveCaret) {
      editor.setContent(value || '');
      return;
    }

    let hadFocus = editor.hasFocus && editor.hasFocus();
    let bookmark = null;
    if (hadFocus) {
      try {
        bookmark = editor.selection.getBookmark(2, true);
      } catch {
        bookmark = null;
      }
    }

    editor.setContent(value || '');

    if (bookmark) {
      try {
        editor.selection.moveToBookmark(bookmark);
      } catch {
        // bookmark may not survive structural changes — leave the caret be.
      }
    }
  });

  // updateOptions reacts to @options changes by tearing down and re-initialising
  // the editor.
  updateOptions = modifier((element, [options]) => {
    // Skip the initial run: setupEditor performs the first init.
    if (options === this.appliedOptions) {
      return;
    }

    this.appliedOptions = options;
    this.teardownEditor();
    this.initEditor(element, options);
  });

  // initEditor lazily loads TinyMCE (once, shared) and initialises an editor on
  // the given element with the given options.
  async initEditor(element, options) {
    // Claim this initialisation; a later init (or teardown-then-init from an
    // @options change) bumps the token, marking everything below as stale.
    const token = ++this.initToken;

    if (!options) {
      // Don't initialise without options.
      return;
    }

    let tinymce;
    try {
      tinymce = await loadTinyMCE();
    } catch {
      // Loading failed; there's nothing to initialise.
      return;
    }

    // Bail if the component was torn down, or a newer init superseded this one,
    // while the script was loading.
    if (this.isDestroying || this.isDestroyed || token !== this.initToken) {
      return;
    }

    // TinyMCE targets the element by id; give the textarea a stable one.
    if (!element.id) {
      element.id = `tinymce-${guidFor(this)}`;
    }

    let customOptions = {
      selector: `#${element.id}`,
      init_instance_callback: (editor) => {
        // TinyMCE calls this on a later tick: a newer init may have started (or
        // the component torn down) in the meantime. Discard this now-stale
        // editor rather than adopting it over the current one.
        if (this.isDestroying || this.isDestroyed || token !== this.initToken) {
          editor.destroy();
          return;
        }
        this.editor = editor;
        editor.setContent(this.args.value || '');
        editor.on(this.tinyEvents, () =>
          this.contentChangedTask.perform(editor),
        );
      },
    };

    tinymce.init(Object.assign({}, options, customOptions));
  }

  // teardownEditor cancels any pending content-change debounce, then unbinds
  // and destroys the current editor, if any. Cancelling explicitly keeps
  // teardown self-contained rather than relying on ember-concurrency's
  // implicit cancellation when the host component is destroyed.
  teardownEditor() {
    this.contentChangedTask.cancelAll();

    let editor = this.editor;
    if (editor) {
      editor.off(this.tinyEvents);
      editor.destroy();
      this.editor = null;
    }
  }

  // contentChangedTask debounces content-change handling. `restartable` means a
  // fresh change restarts the timer, so the check runs once the edits settle.
  contentChangedTask = restartableTask(async (editor) => {
    await timeout(this.changeDebounce);
    this.contentChanged(editor);
  });

  // contentChanged forwards the editor's content when it has meaningfully
  // changed. `@alwaysForwardContent` bypasses the dirty/equality guard.
  contentChanged(editor) {
    let content = editor.getContent();
    if (this.args.alwaysForwardContent) {
      this.onValueChanged(content);
      return;
    }
    if (editor.isDirty() && content !== this.args.value) {
      this.onValueChanged(content);
      editor.setDirty(true);
    }
  }

  // onValueChanged invokes the consumer's callback, when supplied.
  // Without `@onValueChanged`, edits stay in the editor.
  onValueChanged(value) {
    if (typeof this.args.onValueChanged === 'function') {
      this.args.onValueChanged(value);
    }
  }
}
